import type { TFunction } from "i18next";

/**
 * Client-side mirror of the backend RAG upload gate
 * (`src/utils/ragExtraction/mimeTypes.js` + the `validateUpload` helper in
 * `src/api/rag/controllers/rag.js`). The backend stays the authority; this only
 * exists so the user gets an instant, actionable message instead of a round
 * trip that ends in a generic 400.
 *
 * Keep the two in sync: adding a format here without adding it there produces
 * an upload that the server refuses.
 * See docs/plans/todo/rag-office-formats-lambda-extraction.md.
 */

const PDF = "application/pdf";
const TXT = "text/plain";
const DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XLS = "application/vnd.ms-excel";
const XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const XLSM = "application/vnd.ms-excel.sheet.macroEnabled.12";
const PPTX = "application/vnd.openxmlformats-officedocument.presentationml.presentation";
const PPT = "application/vnd.ms-powerpoint";

const OFFICE_MIMES = [DOCX, XLS, XLSX, XLSM, PPTX];
const ACCEPTED_MIMES = [PDF, TXT, ...OFFICE_MIMES];

/** `accept` attribute for the file inputs. `.ppt` is deliberately absent. */
export const RAG_ACCEPTED_EXTENSIONS = ".pdf,.docx,.txt,.xls,.xlsx,.xlsm,.pptx";

// Per-format caps, mirroring the backend's RAG_MAX_*_FILE_SIZE_MB defaults:
// PDF goes to Textract straight from S3 (nothing to blow up), office formats
// are parsed in memory, and TXT is parsed in-process on a 4 GB web instance.
export const MAX_PDF_SIZE_MB = 300;
export const MAX_OFFICE_SIZE_MB = 100;
export const MAX_TEXT_SIZE_MB = 50;

const EXTENSION_TO_MIME: Record<string, string> = {
  ".pdf": PDF,
  ".txt": TXT,
  ".docx": DOCX,
  ".xls": XLS,
  ".xlsx": XLSX,
  ".xlsm": XLSM,
  ".pptx": PPTX,
  ".ppt": PPT,
};

// What a browser sends when it has no idea what the file is. Chrome on Linux
// reports octet-stream for .xlsm and for office files copied off a network
// share, so without the extension fallback valid uploads get refused here even
// though the backend would have accepted them.
const GENERIC_MIMES = new Set([
  "application/octet-stream",
  "application/binary",
  "binary/octet-stream",
  "application/x-zip-compressed",
  "application/zip",
  "",
]);

// MIME types are case-insensitive per RFC 2045 but two of ours are camel-cased
// ("...sheet.macroEnabled.12"), and every comparison below is an exact string
// match — a blanket toLowerCase() would turn a valid .xlsm into "unsupported".
const CANONICAL_BY_LOWERCASE = new Map(
  [...ACCEPTED_MIMES, PPT].map((mime) => [mime.toLowerCase(), mime]),
);

const extensionOf = (filename: string): string => {
  const match = /(\.[^./\\]+)$/.exec(filename || "");
  return match ? match[1].toLowerCase() : "";
};

/**
 * Trust the type the browser declared unless it is one of the "I don't know"
 * generics, in which case fall back to the extension. Unknown types come back
 * as-is so the caller can still name what was actually dropped.
 */
export const resolveRagMimeType = (declaredType: string, filename: string): string => {
  const declared = (declaredType || "").trim().toLowerCase();
  if (!GENERIC_MIMES.has(declared)) {
    return CANONICAL_BY_LOWERCASE.get(declared) ?? declared;
  }
  return EXTENSION_TO_MIME[extensionOf(filename)] ?? declared;
};

export const maxRagSizeMbFor = (mimeType: string): number => {
  if (mimeType === PDF) return MAX_PDF_SIZE_MB;
  if (OFFICE_MIMES.includes(mimeType)) return MAX_OFFICE_SIZE_MB;
  return MAX_TEXT_SIZE_MB;
};

export type RagUploadRejection =
  | { reason: "legacyPpt" }
  | { reason: "type" }
  | { reason: "size"; maxMb: number };

/**
 * @returns the reason the file cannot be uploaded, or null when it is fine.
 */
export const validateRagUpload = (file: File): RagUploadRejection | null => {
  const mimeType = resolveRagMimeType(file.type, file.name);

  // Legacy binary PowerPoint gets its own message: no pure-JS parser exists for
  // it, but re-saving as .pptx takes seconds, so tell the user that instead of
  // a dead-end "unsupported type".
  if (mimeType === PPT) return { reason: "legacyPpt" };

  if (!ACCEPTED_MIMES.includes(mimeType)) return { reason: "type" };

  const maxMb = maxRagSizeMbFor(mimeType);
  if (file.size > maxMb * 1024 * 1024) return { reason: "size", maxMb };

  return null;
};

export const ragUploadErrorMessage = (rejection: RagUploadRejection, t: TFunction): string => {
  switch (rejection.reason) {
    case "legacyPpt":
      return t("pages.agents.uploadErrorLegacyPpt");
    case "size":
      return t("pages.agents.uploadErrorSize", { max: rejection.maxMb });
    default:
      return t("pages.agents.uploadErrorType");
  }
};
